import {type FieldPath, type FieldValues, type PathValue, useController} from "react-hook-form";
import {type TypeaheadMultiSelectProps, TypeaheadMultiSelect} from "app/employee/incdoc/supplier/TypeaheadMultiSelect";
import {hofForwardRef, type MaybeNil, noop, type RhfProps, type RhfRef} from "app/utils/types";
import {type ForwardedRef, type RefCallback, useCallback, useImperativeHandle, useRef} from "react";
import {assignRef, defaultScrollIntoView, isEqual} from "app/utils/stdlib";

const reportValidity = () => true;

export const RhfTypeaheadMultiSelect = hofForwardRef(
  <
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>,
    Value extends PathValue<TFieldValues, TName> extends Array<infer V> ? V : never,
    Option
  >(
    {
      rhf,
      disabled,
      optionMapper,
      ...rest
    }: Omit<TypeaheadMultiSelectProps<Option, Value>, "onSelect" | "value" | "isEqual"> &
      RhfProps<TFieldValues, TName> & {
        optionMapper: (o: MaybeNil<Option>) => MaybeNil<Value>;
      },
    ref: ForwardedRef<HTMLIonItemElement>
  ) => {
    const {
      field: {onChange, value, disabled: rhfDisabled, ref: fieldRef},
      formState: {isSubmitting},
      fieldState: {error}
    } = useController(rhf);

    const rhfRef = useRef<RhfRef | null>(null);
    const inputRef: RefCallback<HTMLIonItemElement> = (element) => {
      assignRef(ref, element);
      rhfRef.current = {
        focus: () => element?.scrollIntoView(defaultScrollIntoView),
        select: noop,
        setCustomValidity: noop,
        reportValidity
      };
    };
    useImperativeHandle(fieldRef, () => rhfRef.current);

    const isEqualOptionToValue = useCallback(
      (option: Option, v: Value) => {
        return isEqual(optionMapper(option), v);
      },
      [optionMapper]
    );

    return (
      <TypeaheadMultiSelect
        isEqual={isEqualOptionToValue}
        ref={inputRef}
        {...rest}
        disabled={isSubmitting || disabled || rhfDisabled}
        errorMessage={error?.message}
        onSelect={(options) => {
          const mapped = options?.map(optionMapper).filter((v): v is Value => v != null) ?? [];
          onChange(mapped);
        }}
        value={value}
      />
    );
  }
);
